import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { KeyRound, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { config } from '@/config';
import { CeremonyDismissed, CeremonyUnsupported, getAssertion, isWebAuthnAvailable } from '@/lib/webauthn';
import { completeSignIn } from '@/lib/activeAccount';
import { tooManyAccountsMessage } from './tooManyAccounts';

/**
 * Signing in with a passkey alone — no password (`docs/WebAuthn.md` §1, §4.2).
 *
 * ⚠ **The address is optional, and deliberately so.** Sent, the server answers with an
 * allow-list; omitted, the authenticator offers whatever discoverable credential it holds for
 * this site. Either way the response is *shaped identically for an address that has no
 * passkeys*, because the allow-list is fabricated when there are none — so nothing here reveals
 * whether an account exists, and this component must not try to be clever about that.
 *
 * A failure therefore cannot be reported as "no passkey for that address" without undoing the
 * chaff. One message covers every outcome.
 */
export function PasskeySignInButton({ email }: { email?: string }) {
  const queryClient = useQueryClient();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  if (!isWebAuthnAvailable()) return null;

  const signIn = () => {
    setError(undefined);
    setPending(true);
    void axios
      .post<{ challengeId: string; challenge: { publicKey: Record<string, unknown> } }>(
        `${config.backendApi}/api/auth/webauthn/login/start`,
        // Blank is omitted rather than sent as "", so the server runs the discoverable path.
        { email: email?.trim() ? email.trim() : undefined },
        { withCredentials: true },
      )
      .then(async ({ data }) => {
        const credential = await getAssertion(data.challenge);
        // With no email typed, a passkey can sign in any account on the authenticator, so the
        // tab selects whichever one the response names.
        const { data: session } = await axios.post<unknown>(
          `${config.backendApi}/api/auth/webauthn/login/finish`,
          { challengeId: data.challengeId, credential },
          { withCredentials: true },
        );
        await completeSignIn(queryClient, session);
      })
      .catch((e: unknown) => {
        if (e instanceof CeremonyUnsupported) console.error('Passkey sign-in refused', e.cause);
        setError(
          tooManyAccountsMessage(e) ??
            (e instanceof CeremonyDismissed
              ? 'That prompt was dismissed.'
              : 'Could not sign in with a passkey. Use your password instead.'),
        );
      })
      .finally(() => { setPending(false); });
  };

  return (
    <div className="space-y-2">
      <Button
        type="button"
        variant="outline"
        className="w-full"
        data-testid="passkey-signin"
        disabled={pending}
        onClick={signIn}
      >
        {pending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <KeyRound className="mr-2 h-4 w-4" />}
        {pending ? 'Waiting for your passkey…' : 'Sign in with a passkey'}
      </Button>
      {error && <p className="text-center text-xs text-destructive">{error}</p>}
    </div>
  );
}
